// AgentOS rule for the nQube runner: a model may propose, a non-model gate
// must sit on every path to a side effect, and the side effect must name a
// capability declared on the pipeline. Copied to
// frontend/src/data/agentosGraph.js by scripts/generate-entities.mjs.
// No Node imports — the browser copy has to stay runnable.

const MODEL_TYPES = new Set(['transform.llm', 'loop.foreach', 'loop.refine', 'control.coordinate']);

const KIND_BY_TYPE = {
  'output.s3write': 's3',
  'output.dataset': 'dataset',
  'output.knowledge': 'knowledge',
  'container.run': 'container',
  'output.webhook': 'webhook',
};

const SECRET_KEY = /^[a-zA-Z0-9][a-zA-Z0-9_-]{0,63}$/;

export function sideEffectKind(type) {
  return KIND_BY_TYPE[type] || null;
}

export function isModelBlock(block) {
  if (!block) return false;
  if (MODEL_TYPES.has(block.type)) return true;
  return block.type === 'control.filter' && (block.config?.mode || 'contains') === 'llm';
}

export function isGateBlock(block) {
  if (!block) return false;
  if (block.type === 'control.review') return true;
  return block.type === 'control.filter' && (block.config?.mode || 'contains') !== 'llm';
}

function labelOf(block) {
  return block?.label || block?.type || 'block';
}

function capabilityList(capabilities) {
  return Array.isArray(capabilities) ? capabilities : [];
}

// One side-effect block against the manifest. `resolved.key` is the object
// key the handler will actually write (defaults differ from the config).
export function capabilityProblem(block, capabilities, resolved = {}) {
  const kind = sideEffectKind(block?.type);
  if (!kind) return null;
  const label = labelOf(block);
  const caps = capabilityList(capabilities);
  const id = block.config?.capabilityId;
  const cap = caps.find((c) => c && c.id === id);
  if (!cap) return `${label} names no declared capability`;
  if (cap.kind !== kind) return `${label} capability '${cap.id}' is ${cap.kind || 'unset'}, need ${kind}`;
  const target = String(cap.target || '');
  if (!target) return `${label} capability '${cap.id}' has no target`;
  if (kind === 's3') {
    const key = resolved.key != null ? String(resolved.key) : String(block.config?.key || '');
    if (key && !key.startsWith(target)) return `${label} key must start with capability target '${target}'`;
    if (!key && target !== 'pipelines' && target !== 'pipelines/') {
      return `${label} uses the default pipelines/ key; set the capability target to pipelines/ or set a key under '${target}'`;
    }
  } else if (kind === 'dataset') {
    if (String(block.config?.datasetId || '') !== target) return `${label} datasetId must equal capability target '${target}'`;
  } else if (kind === 'knowledge') {
    const audience = block.config?.audience === 'internal' ? 'internal' : 'public';
    if (audience !== target) return `${label} audience must equal capability target '${target}'`;
  } else if (kind === 'container') {
    const image = String(block.config?.image || '');
    if (!image.startsWith(target)) return `${label} image must start with capability target '${target}'`;
  } else if (kind === 'webhook') {
    let url;
    try { url = new URL(target); } catch { return `${label} webhook target is not a URL`; }
    if (url.protocol !== 'https:') return `${label} webhook target must be https`;
    if (!SECRET_KEY.test(String(cap.secretKey || ''))) {
      return `${label} webhook capability needs a secretKey name (SSM pipeline-secrets/<name>), not the secret itself`;
    }
  }
  return null;
}

export function capabilityFor(block, capabilities, resolved) {
  const problem = capabilityProblem(block, capabilities, resolved);
  if (problem) throw new Error(problem);
  return capabilityList(capabilities).find((c) => c && c.id === block.config?.capabilityId);
}

// Every problem that should refuse a run. Empty means the graph may run.
export function agentOsProblems(graph, capabilities) {
  const blocks = graph?.blocks || [];
  const conns = graph?.connections || [];
  const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
  const outs = Object.fromEntries(blocks.map((b) => [b.id, []]));
  for (const c of conns) {
    if (outs[c.from]) outs[c.from].push(c.to);
  }
  const problems = [];
  for (const block of blocks) {
    const problem = capabilityProblem(block, capabilities);
    if (problem) problems.push(problem);
  }
  for (const model of blocks.filter(isModelBlock)) {
    const seen = new Set();
    const stack = [[model.id, false]];
    while (stack.length) {
      const [id, gated] = stack.pop();
      const key = `${id}:${gated ? 1 : 0}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const node = byId[id];
      if (!node) continue;
      if (id !== model.id && sideEffectKind(node.type) && !gated) {
        problems.push(
          `${labelOf(model)} can reach ${labelOf(node)} with no gate — put a human review or a keyword filter on that path`
        );
      }
      const next = gated || isGateBlock(node);
      for (const to of outs[id] || []) stack.push([to, next]);
    }
  }
  return [...new Set(problems)];
}

export function assertAgentOsGraph(graph, capabilities) {
  const problems = agentOsProblems(graph, capabilities);
  if (problems.length) throw new Error(problems.join('; '));
}
